import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Users, Plus, Clock, CheckCircle, XCircle, Mail, Phone, 
  Calendar, ArrowUp, ArrowDown, Trash2, Send, Baby, Building
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const WaitlistPage = () => {
  const { getAuthHeaders } = useAuth();
  const [waitlist, setWaitlist] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    child_name: '',
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_dob: '',
    preferred_classroom: '',
    preferred_start_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchWaitlist();
  }, []);

  const fetchWaitlist = async () => {
    try {
      const res = await axios.get(`${API}/waitlist`, getAuthHeaders());
      setWaitlist(res.data.waitlist || []);
      setClassrooms(res.data.classrooms || []);
    } catch (error) {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToWaitlist = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/waitlist`, formData, getAuthHeaders());
      toast.success('Added to waitlist');
      setAddDialogOpen(false);
      setFormData({
        child_name: '',
        parent_name: '',
        parent_email: '',
        parent_phone: '',
        child_dob: '',
        preferred_classroom: '',
        preferred_start_date: '',
        notes: ''
      });
      fetchWaitlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to waitlist');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/waitlist/${id}/status?status=${status}`, {}, getAuthHeaders());
      toast.success(`Status updated to ${status}`);
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const movePosition = async (id, direction) => {
    const entry = waitlist.find(w => w.id === id);
    if (!entry) return;
    
    const newPosition = direction === 'up' ? entry.position - 1 : entry.position + 1;
    if (newPosition < 1) return;
    
    try {
      await axios.put(`${API}/waitlist/${id}/position?new_position=${newPosition}`, {}, getAuthHeaders());
      toast.success('Position updated');
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to update position');
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to remove this entry from the waitlist?')) return;
    
    try {
      await axios.delete(`${API}/waitlist/${id}`, getAuthHeaders());
      toast.success('Removed from waitlist');
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to remove entry');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      waiting: 'bg-yellow-100 text-yellow-800',
      offered: 'bg-blue-100 text-blue-800',
      enrolled: 'bg-green-100 text-green-800',
      removed: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const waitingCount = waitlist.filter(w => w.status === 'waiting').length;
  const offeredCount = waitlist.filter(w => w.status === 'offered').length;

  if (loading) {
    return (
      <DashboardLayout title="Waitlist">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Waitlist Management">
      <div className="space-y-6" data-testid="waitlist-page">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-clay">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{waitingCount}</p>
                  <p className="text-sm text-muted-foreground">Waiting</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-clay">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{offeredCount}</p>
                  <p className="text-sm text-muted-foreground">Spots Offered</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {classrooms.slice(0, 2).map((classroom) => (
            <Card key={classroom.id} className="card-clay">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{classroom.available_spots} spots</p>
                    <p className="text-sm text-muted-foreground">{classroom.name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Classroom Availability */}
        <Card className="card-clay">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building className="w-5 h-5 text-primary" />
              Classroom Availability
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {classrooms.map((classroom) => (
                <div key={classroom.id} className="p-4 bg-muted/50 rounded-xl">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{classroom.name}</h4>
                    <Badge variant={classroom.available_spots > 0 ? 'default' : 'destructive'}>
                      {classroom.available_spots > 0 ? `${classroom.available_spots} available` : 'Full'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {classroom.enrolled || 0} / {classroom.capacity} enrolled
                  </p>
                  {classroom.age_range && (
                    <p className="text-xs text-muted-foreground mt-1">Age: {classroom.age_range}</p>
                  )}
                  <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${classroom.available_spots > 0 ? 'bg-primary' : 'bg-destructive'}`}
                      style={{ width: `${Math.min(100, ((classroom.enrolled || 0) / classroom.capacity) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
              {classrooms.length === 0 && (
                <p className="text-muted-foreground col-span-3 text-center py-4">
                  No classrooms configured. Add classrooms in Settings.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Waitlist */}
        <Card className="card-clay">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Waitlist
              </CardTitle>
              <CardDescription>Manage families waiting for enrollment</CardDescription>
            </div>
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-to-waitlist-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Waitlist
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">Add to Waitlist</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddToWaitlist} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="child_name">Child&apos;s Name *</Label>
                      <Input
                        id="child_name"
                        value={formData.child_name}
                        onChange={(e) => setFormData({...formData, child_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="child_dob">Date of Birth *</Label>
                      <Input
                        id="child_dob"
                        type="date"
                        value={formData.child_dob}
                        onChange={(e) => setFormData({...formData, child_dob: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="parent_name">Parent Name *</Label>
                      <Input
                        id="parent_name"
                        value={formData.parent_name}
                        onChange={(e) => setFormData({...formData, parent_name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="parent_email">Email *</Label>
                      <Input
                        id="parent_email"
                        type="email"
                        value={formData.parent_email}
                        onChange={(e) => setFormData({...formData, parent_email: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="parent_phone">Phone *</Label>
                      <Input
                        id="parent_phone"
                        type="tel"
                        value={formData.parent_phone}
                        onChange={(e) => setFormData({...formData, parent_phone: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferred_classroom">Preferred Classroom</Label>
                      <Select 
                        value={formData.preferred_classroom} 
                        onValueChange={(v) => setFormData({...formData, preferred_classroom: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select classroom" />
                        </SelectTrigger>
                        <SelectContent>
                          {classrooms.map((c) => (
                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferred_start_date">Preferred Start Date</Label>
                    <Input
                      id="preferred_start_date"
                      type="date"
                      value={formData.preferred_start_date}
                      onChange={(e) => setFormData({...formData, preferred_start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Any additional notes..."
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Add to Waitlist</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {waitlist.length > 0 ? (
              <div className="space-y-4">
                {waitlist.filter(w => w.status === 'waiting' || w.status === 'offered').map((entry, index) => (
                  <div 
                    key={entry.id}
                    className="p-4 bg-muted/50 rounded-xl"
                    data-testid={`waitlist-entry-${entry.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="flex flex-col items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => movePosition(entry.id, 'up')}
                            disabled={entry.position === 1 || entry.status !== 'waiting'}
                            className="h-6 w-6 p-0"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <span className="font-bold text-lg">#{entry.position}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => movePosition(entry.id, 'down')}
                            disabled={entry.status !== 'waiting'}
                            className="h-6 w-6 p-0"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Baby className="w-4 h-4 text-primary" />
                            <h4 className="font-semibold">{entry.child_name}</h4>
                            {getStatusBadge(entry.status)}
                          </div>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p className="flex items-center gap-2">
                              <Users className="w-3 h-3" />
                              {entry.parent_name}
                            </p>
                            <p className="flex items-center gap-2">
                              <Mail className="w-3 h-3" />
                              {entry.parent_email}
                            </p>
                            <p className="flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {entry.parent_phone}
                            </p>
                            {entry.child_dob && (
                              <p className="flex items-center gap-2">
                                <Calendar className="w-3 h-3" />
                                DOB: {entry.child_dob}
                              </p>
                            )}
                          </div>
                          {entry.preferred_classroom && (
                            <Badge variant="outline" className="mt-2">
                              Preferred: {entry.preferred_classroom}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {entry.status === 'waiting' && (
                          <Button 
                            size="sm"
                            onClick={() => updateStatus(entry.id, 'offered')}
                            data-testid={`offer-spot-${entry.id}`}
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Offer Spot
                          </Button>
                        )}
                        {entry.status === 'offered' && (
                          <Button 
                            size="sm"
                            onClick={() => updateStatus(entry.id, 'enrolled')}
                            data-testid={`enroll-${entry.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Enroll
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(entry.id, 'removed')}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                        <Button 
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deleteEntry(entry.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {entry.notes && (
                      <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
                        <strong>Notes:</strong> {entry.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No one on the waitlist</p>
                <p className="text-sm">Add families waiting for enrollment</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recently Enrolled/Removed */}
        {waitlist.filter(w => w.status === 'enrolled' || w.status === 'removed').length > 0 && (
          <Card className="card-clay">
            <CardHeader>
              <CardTitle className="font-heading text-base">Recently Processed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {waitlist.filter(w => w.status === 'enrolled' || w.status === 'removed').slice(0, 5).map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Baby className="w-4 h-4 text-muted-foreground" />
                      <span>{entry.child_name}</span>
                      <span className="text-muted-foreground">({entry.parent_name})</span>
                    </div>
                    {getStatusBadge(entry.status)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WaitlistPage;
